/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports a function, not a React component. */
// `:not([tabindex="-1"])` on every branch, not just the bare `[tabindex]` one -- a `<button>` (or
// any other naturally-focusable element) explicitly marked `tabIndex={-1}` is a deliberate
// pointer-only affordance (e.g. DimeEditableCell's own clear button), not a real Tab stop; the
// original selector only excluded that combination on plain elements, so a tabIndex={-1} button
// still matched `button:not([disabled])` and was wrongly treated as "the next cell" here.
const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Finds the next (or, with `direction: -1`, previous) focusable element in document order.
 *
 * Committing on Tab exits edit mode and removes `current` from the DOM in the same synchronous
 * event-handling pass that a real browser's (or `userEvent`'s simulated) default Tab action uses
 * to compute "what's focusable now" — by the time that computation runs, `current` is already
 * gone, and the algorithm falls back to `document.body`, dropping focus instead of advancing it.
 * Capturing the destination here, before anything commits, and focusing it explicitly (with
 * `preventDefault` to suppress the native/simulated default action) sidesteps that race instead
 * of depending on timing that isn't guaranteed either way.
 */
export function findAdjacentFocusable(current: Element, direction: 1 | -1): HTMLElement | null {
  const candidates = Array.from(
    current.ownerDocument.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  const index = candidates.indexOf(current as HTMLElement);
  if (index === -1) return null;
  return candidates[index + direction] ?? null;
}
